import React, { useState, useEffect, useRef } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import moment from "moment";
import Header from "../../Common/Layout/Header";
import Login from "../../Authentication/Login";
import config from "../../Common/Configurations/APIConfig";
import apiService from "../../Common/Configurations/APIService";
import { useAuth } from "../../Common/Configurations/AuthContext";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";
import "./PurchaseView.css";

const PurchaseView = () => {
  const { user, loading } = useAuth();
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const [invoice, setInvoice] = useState(null);
  const [error, setError] = useState(null);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const invoiceRef = useRef();

  const GetData = async () => {
    if (invoiceId) {
      try {
        const data = await apiService.getById(config.GETINVOICEBYID, invoiceId);
        setInvoice(data);
        setError(null);
      } catch (err) {
        setError("Failed to fetch purchase invoice details. Please try again.");
        console.error(err);
      } finally {
      }
    }
  };

  useEffect(() => {
    if (!loading && user && invoiceId) {
      GetData();
    }
  }, [user, loading, invoiceId]);

  // Debug user object
  useEffect(() => {
    console.log("👤 Current User Object:", user);
    if (user) {
      console.log("📸 User Logo Property:", user.logo);
      console.log("🔍 All User Properties:", Object.keys(user));
    }
  }, [user]);

  const downloadPDF = async () => {
    if (!invoiceRef.current) return;

    setIsGeneratingPDF(true);
    try {
      const element = invoiceRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        useCORS: true,
        logging: false,
        backgroundColor: "#ffffff",
      });

      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");
      const pdfWidth = pdf.internal.pageSize.getWidth();
      const pdfHeight = pdf.internal.pageSize.getHeight();

      const imgWidth = canvas.width;
      const imgHeight = canvas.height;
      const ratio = Math.min(pdfWidth / imgWidth, pdfHeight / imgHeight);
      const imgX = (pdfWidth - imgWidth * ratio) / 2;
      const imgY = 10;

      pdf.addImage(
        imgData,
        "PNG",
        imgX,
        imgY,
        imgWidth * ratio,
        imgHeight * ratio
      );

      const fileName = `purchase-invoice-${
        invoice.invoiceNumber || invoice.invoiceId
      }.pdf`;
      pdf.save(fileName);
    } catch (error) {
      console.error("Error generating PDF:", error);
      alert("Failed to generate PDF. Please try again.");
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const printInvoice = () => {
    window.print();
  };

  if (loading) return <p>Loading...</p>;

  if (!user) {
    navigate("/");
  }

  return (
    <div id="layout-wrapper">
      <Header User={user} />
      <div className="main-content">
        <div className="page-content">
          <div className="container-fluid">
            <div className="row">
              <div className="col-xl-12">
                <div className="card">
                  <div className="card-body">
                    {loading && (
                      <div className="text-center p-5">
                        <p>Loading purchase invoice...</p>
                      </div>
                    )}
                    {error && <div className="alert alert-danger">{error}</div>}

                    {!loading && !error && invoice && (
                      <div className="purchaseview-invoice-wrapper">
                        <div className="purchaseview-view-actions-bar">
                          <button
                            onClick={() => navigate("/purchaselist")}
                            className="btn btn-secondary"
                          >
                            <i className="ri-arrow-left-line align-bottom me-1" />{" "}
                            Back To List
                          </button>
                          <div>
                            <button
                              onClick={downloadPDF}
                              disabled={isGeneratingPDF}
                              className="btn btn-success me-2"
                            >
                              <i className="ri-download-line align-bottom me-1" />
                              {isGeneratingPDF
                                ? "Generating PDF..."
                                : "Download PDF"}
                            </button>
                            <button
                              onClick={printInvoice}
                              className="btn btn-info me-2"
                            >
                              <i className="ri-printer-line align-bottom me-1" />
                              Print
                            </button>
                            <Link
                              to={`/editpurchaseinvoice/${invoice.invoiceId}`}
                              className="btn btn-primary"
                            >
                              <i className="ri-pencil-fill align-bottom me-1" />{" "}
                              Edit Invoice
                            </Link>
                          </div>
                        </div>

                        <div
                          ref={invoiceRef}
                          className="purchaseview-invoice-container"
                          id="invoice-to-print"
                        >
                          <header className="purchaseview-invoice-header">
                            <div className="purchaseview-from-details">
                              {/* Company Logo with Static Fallback */}
                              <div className="purchaseview-company-logo">
                                {user.logo ? (
                                  <img
                                    src={user.logo}
                                    alt="Company Logo"
                                    className="purchaseview-logo-img"
                                    onError={(e) => {
                                      console.error(
                                        "Logo failed to load, using static fallback"
                                      );
                                      e.target.style.display = "none";
                                      // You can set a fallback image here if needed
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      fontSize: "12px",
                                      color: "#666",
                                      padding: "10px",
                                      background: "#f8f9fa",
                                      borderRadius: "4px",
                                      marginBottom: "10px",
                                    }}
                                  >
                                    No company logo available in user profile
                                  </div>
                                )}
                              </div>

                              <h3 className="purchaseview-company-name">
                                {invoice.companyDetails.companyName}
                              </h3>

                              <p>{invoice.companyDetails.address}</p>
                              <p>{`${invoice.companyDetails.city}, ${invoice.companyDetails.state}`}</p>
                              <p>
                                <strong>Email:</strong>{" "}
                                {invoice.companyDetails.email}
                              </p>
                              <p>
                                <strong>Phone:</strong>{" "}
                                {invoice.companyDetails.phoneNumber}
                              </p>
                            </div>
                            <div className="purchaseview-invoice-title">
                              <h1>INVOICE</h1>
                            </div>
                          </header>

                          <section className="purchaseview-invoice-meta-details">
                            <div className="purchaseview-to-details">
                              <h4>BILL TO:</h4>
                              <p>
                                <strong>
                                  {invoice.vendorDetails.vendorName}
                                </strong>
                              </p>
                              <p>{invoice.vendorDetails.phoneNumber}</p>
                              <p>{invoice.vendorDetails.address}</p>
                              <p>{`${invoice.vendorDetails.city}, ${invoice.vendorDetails.state} - ${invoice.vendorDetails.postalCode}`}</p>
                            </div>
                            <div className="purchaseview-meta-info">
                              <p>
                                <strong>Invoice No:</strong>{" "}
                                <span>{invoice.invoiceNumber}</span>
                              </p>
                              <p>
                                <strong>Invoice Date:</strong>{" "}
                                <span>
                                  {moment(invoice.invoiceDate).format(
                                    "DD-MMM-YYYY"
                                  )}
                                </span>
                              </p>
                              <p>
                                <strong>Due Date:</strong>{" "}
                                <span>
                                  {moment(invoice.dueDate).format(
                                    "DD-MMM-YYYY"
                                  )}
                                </span>
                              </p>
                              <p>
                                <strong>Status:</strong>{" "}
                                <span
                                  className={`purchaseview-status-badge purchaseview-status-${invoice.status?.toLowerCase()}`}
                                >
                                  {invoice.status}
                                </span>
                              </p>
                            </div>
                          </section>

                          <section className="purchaseview-invoice-items">
                            <table className="purchaseview-items-table">
                              <thead>
                                <tr>
                                  <th>S.No</th>
                                  <th>Item Description</th>
                                  <th className="purchaseview-text-center">
                                    Quantity
                                  </th>
                                  <th className="purchaseview-text-right">
                                    Unit Price
                                  </th>
                                  <th className="purchaseview-text-right">
                                    Amount
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {invoice.items.map((item, index) => (
                                  <tr key={item.invoiceItemId}>
                                    <td>{index + 1}</td>
                                    <td>{item.description}</td>
                                    <td className="purchaseview-text-center">
                                      {item.quantity}
                                    </td>
                                    <td className="purchaseview-text-right">{`${
                                      invoice.currencyCode
                                    } ${item.unitPrice.toFixed(2)}`}</td>
                                    <td className="purchaseview-text-right">{`${
                                      invoice.currencyCode
                                    } ${(
                                      item.quantity * item.unitPrice
                                    ).toFixed(2)}`}</td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </section>

                          <footer className="purchaseview-invoice-footer">
                            <div className="purchaseview-notes-and-terms">
                              {invoice.notes && (
                                <>
                                  <h4>Notes</h4>
                                  <p>{invoice.notes}</p>
                                </>
                              )}
                              {invoice.terms && (
                                <>
                                  <h4>Terms & Conditions</h4>
                                  <p>{invoice.terms}</p>
                                </>
                              )}
                            </div>
                            <div className="purchaseview-invoice-summary">
                              <div className="purchaseview-summary-row">
                                <span>Subtotal</span>
                                <span>{`${
                                  invoice.currencyCode
                                } ${invoice.subtotal.toFixed(2)}`}</span>
                              </div>
                              <div className="purchaseview-summary-row">
                                <span>Discount</span>
                                <span>
                                  -{" "}
                                  {`${
                                    invoice.currencyCode
                                  } ${invoice.discount.toFixed(2)}`}
                                </span>
                              </div>
                              <div className="purchaseview-summary-row">
                                <span>Tax</span>
                                <span>
                                  +{" "}
                                  {`${
                                    invoice.currencyCode
                                  } ${invoice.tax.toFixed(2)}`}
                                </span>
                              </div>
                              {invoice.taxDetails &&
                                Object.entries(invoice.taxDetails).map(
                                  ([taxName, taxRate]) => (
                                    <div
                                      className="purchaseview-summary-row purchaseview-tax-detail"
                                      key={taxName}
                                    >
                                      <span>
                                        {taxName} @ {taxRate}%
                                      </span>
                                      <span></span>
                                    </div>
                                  )
                                )}
                              <div className="purchaseview-summary-row purchaseview-grand-total">
                                <span>Grand Total</span>
                                <span>{`${
                                  invoice.currencyCode
                                } ${invoice.grandTotal.toFixed(2)}`}</span>
                              </div>
                            </div>
                          </footer>

                          {invoice.bankAccountDetails && (
                            <section className="purchaseview-bank-details-section">
                              <h4>Bank Details</h4>
                              <p>
                                <strong>Bank:</strong>{" "}
                                {invoice.bankAccountDetails.bankName}
                              </p>
                              <p>
                                <strong>Account Number:</strong>{" "}
                                {invoice.bankAccountDetails.accountNumber}
                              </p>
                              <p>
                                <strong>IFSC Code:</strong>{" "}
                                {invoice.bankAccountDetails.ifscCode}
                              </p>
                            </section>
                          )}
                        </div>
                      </div>
                    )}

                    {!loading && !error && !invoice && (
                      <div className="text-center p-5">
                        <p>Purchase invoice not found.</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PurchaseView;
